// packages/theme/index.ts
// Theme tokens + CSS variables injector + Telegram/system sync + dark mode + persistence + CSP nonce

export type Theme = {
  colors: {
    background: string;
    accent: string;
    text: string;
    ageverifButtonBg: string;
    ageverifButtonText: string;
    cardBg: string;
    success?: string;
    warning?: string;
    danger?: string;
    border?: string;
    muted?: string;
    overlay?: string;
    focus?: string;     // NEW: focus ring color
  };
  fonts: { body: string };
  fontSizes: { base: string; small: string; button: string; heading: string; title: string };
  spacing: { xs: string; sm: string; md: string; lg: string };
  radii: { sm: string; md: string; lg: string };
  layout: { maxWidth: string; minHeight: string; borderRadius: string; padding: string };
};

export type Mode = 'light' | 'dark';

export const lightTheme: Theme = {
  colors: {
    background: '#fcf9f4',
    accent:     '#fdf9f3',
    text:       '#4E4135',
    ageverifButtonBg:   '#ffffff',
    ageverifButtonText: '#000000',
    cardBg:     '#ffffff',
    success:    '#2e7d32',
    warning:    '#b26a00',
    danger:     '#b3261e',
    border:     'rgba(0,0,0,0.08)',
    muted:      'rgba(78,65,53,0.7)',
    overlay:    'rgba(0,0,0,0.4)',
    focus:      '#5e9ed6'
  },
  fonts: { body: 'Inter, sans-serif' },
  fontSizes: { base: '16px', small: '14px', button: '16px', heading: '20px', title: '24px' },
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px' },
  radii: { sm: '6px', md: '12px', lg: '16px' },
  layout: { maxWidth: '400px', minHeight: '600px', borderRadius: '16px', padding: '24px' }
};

export const darkTheme: Theme = {
  colors: {
    background: '#12100e',
    accent:     '#1b1917',
    text:       '#efe7dd',
    ageverifButtonBg:   '#ffffff',    // stays white for brand consistency
    ageverifButtonText: '#000000',
    cardBg:     '#171513',
    success:    '#8BC34A',
    warning:    '#FFC107',
    danger:     '#FF6B6B',
    border:     'rgba(255,255,255,0.12)',
    muted:      'rgba(239,231,221,0.7)',
    overlay:    'rgba(0,0,0,0.6)',
    focus:      '#7bb6ff'
  },
  fonts: { body: 'Inter, sans-serif' },
  fontSizes: { base: '16px', small: '14px', button: '16px', heading: '20px', title: '24px' },
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px' },
  radii: { sm: '6px', md: '12px', lg: '16px' },
  layout: { maxWidth: '400px', minHeight: '600px', borderRadius: '16px', padding: '24px' }
};

// Back-compat alias
export const defaultTheme = lightTheme;

const STYLE_ID = 'app-theme-vars';
const STORAGE_KEY = 'app.theme';
let tgUnsub: (() => void) | null = null;

type ApplyOpts = { nonce?: string; id?: string };

export function getThemeFor(mode: Mode): Theme {
  return mode === 'dark' ? darkTheme : lightTheme;
}

// Safe CSS injection (SSR-guarded)
export function applyTheme(theme: Theme, mode: Mode = 'light', opts: ApplyOpts = {}) {
  if (typeof document === 'undefined') return; // SSR/tests

  const styleId = opts.id || STYLE_ID;
  const css = `
:root {
  --bg: ${theme.colors.background};
  --fg: ${theme.colors.text};
  --accent: ${theme.colors.accent};
  --card-bg: ${theme.colors.cardBg};
  --border: ${theme.colors.border};
  --muted: ${theme.colors.muted};
  --overlay: ${theme.colors.overlay};
  --focus: ${theme.colors.focus};

  --btn-av-bg: ${theme.colors.ageverifButtonBg};
  --btn-av-fg: ${theme.colors.ageverifButtonText};

  --font-body: ${theme.fonts.body};

  --fs-base: ${theme.fontSizes.base};
  --fs-small: ${theme.fontSizes.small};
  --fs-button: ${theme.fontSizes.button};
  --fs-heading: ${theme.fontSizes.heading};
  --fs-title: ${theme.fontSizes.title};

  --sp-xs: ${theme.spacing.xs};
  --sp-sm: ${theme.spacing.sm};
  --sp-md: ${theme.spacing.md};
  --sp-lg: ${theme.spacing.lg};

  --r-sm: ${theme.radii.sm};
  --r-md: ${theme.radii.md};
  --r-lg: ${theme.radii.lg};

  --layout-max: ${theme.layout.maxWidth};
  --layout-minh: ${theme.layout.minHeight};
  --layout-pad: ${theme.layout.padding};
}

/* Respect reduced motion for smoother UX */
@media (prefers-reduced-motion: reduce) {
  * { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; scroll-behavior: auto !important; }
}

html { color-scheme: ${mode}; }
body {
  margin: 0;
  font-family: var(--font-body);
  color: var(--fg);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
`;
  let tag = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = styleId;
    if (opts.nonce) tag.setAttribute('nonce', opts.nonce);
    document.head.appendChild(tag);
  }
  tag.textContent = css;
  document.documentElement.dataset.theme = mode;
}

export function setThemeMode(mode: Mode, persist = true, opts?: ApplyOpts) {
  const theme = getThemeFor(mode);
  applyTheme(theme, mode, opts);
  if (persist && typeof localStorage !== 'undefined') {
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }
}

// Telegram/system sync with user-preference precedence:
// 1) user-saved (localStorage)
// 2) Telegram WebApp colorScheme
// 3) system prefers-color-scheme
// 4) default: light
export function initTheme(opts?: ApplyOpts) {
  // Telegram ready() is safe if present
  try { (window as any)?.Telegram?.WebApp?.ready?.(); } catch {}

  const saved = getSavedMode();
  if (saved) {
    setThemeMode(saved, false, opts);
    // If user set a preference, we do NOT auto-follow Telegram.
    cleanupTelegramListener();
    return;
  }

  // No saved preference → follow Telegram, else system.
  const tg = (window as any)?.Telegram?.WebApp;
  const scheme = tg?.colorScheme as Mode | undefined;

  if (scheme === 'dark' || scheme === 'light') {
    setThemeMode(scheme, false, opts);
    // subscribe to themeChanged
    if (tg?.onEvent) {
      const handler = () => {
        const next = tg.colorScheme === 'dark' ? 'dark' : 'light';
        setThemeMode(next, false, opts);
      };
      tg.onEvent('themeChanged', handler);
      tgUnsub = () => {
        try { tg.offEvent?.('themeChanged', handler); } catch {}
        tgUnsub = null;
      };
    }
  } else {
    const prefersDark = typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    setThemeMode(prefersDark ? 'dark' : 'light', false, opts);
  }
}

function getSavedMode(): Mode | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch { return null; }
}

export function clearSavedTheme() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export function cleanupTelegramListener() {
  if (tgUnsub) { tgUnsub(); }
}

// Utility: shallow merge for brand overrides (e.g., per-app tweaks)
export function createTheme(base: Theme, patch: Partial<Theme>): Theme {
  return {
    ...base,
    colors: { ...base.colors, ...(patch.colors || {}) },
    fonts: { ...base.fonts, ...(patch.fonts || {}) },
    fontSizes: { ...base.fontSizes, ...(patch.fontSizes || {}) },
    spacing: { ...base.spacing, ...(patch.spacing || {}) },
    radii: { ...base.radii, ...(patch.radii || {}) },
    layout: { ...base.layout, ...(patch.layout || {}) }
  };
}
